// Shared by the browser form and the submit-intake function. No imports: callers pass their own zod.
// deno-lint-ignore-file no-explicit-any
export const STARTING_POINTS = ['New brand', 'Existing clinic or practice', 'Existing telehealth brand', 'Other'] as const;
export const PRIMARY_NEEDS = ['Launch a brand', 'Patient acquisition', 'Paid media', 'Creative', 'Not sure yet'] as const;
export const AD_BUDGETS = ['Not running ads yet', 'Under $10K', '$10K-$25K', '$25K-$50K', '$50K-$100K', '$100K+'] as const;
export const REVENUES = ['$0-$50K', '$50K-$100K', '$100K-$500K', '$500K-$1M', '$1M+', 'Prefer not to say'] as const;
export const CHANNELS = ['Meta', 'Google', 'TikTok', 'Other', 'Not running ads'] as const;
export const ATTRIBUTION_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid'] as const;
export const CONTACT_NOTICE_VERSION = 'intake-contact-notice-2026-10';
export const GOALS_MAX = 2000;

export const INTAKE_LABELS: Record<string, string> = {
  startingPoint: 'Starting point', company: 'Company / brand', website: 'Website', primaryNeed: 'Primary need',
  adBudget: 'Monthly advertising budget', revenue: 'Current monthly revenue', channels: 'Advertising channels',
  heardFrom: 'How they heard about us', goals: 'Goals / context', firstName: 'First name', lastName: 'Last name',
  email: 'Business email', phone: 'Phone',
};
export const STEP_ONE_FIELDS = ['startingPoint', 'company', 'website', 'primaryNeed', 'adBudget', 'revenue', 'channels', 'heardFrom', 'goals'] as const;
export const STEP_TWO_FIELDS = ['firstName', 'lastName', 'email', 'phone'] as const;

/** Adds https:// when a visitor types a bare domain. Returns '' for blank input. */
export function normalizeWebsite(value: string): string {
  const v = value.trim();
  if (!v) return '';
  return /^[a-z][a-z0-9+.-]*:/i.test(v) ? v : `https://${v}`;
}

export function isHttpUrl(value: string): boolean {
  try { const u = new URL(value); return (u.protocol === 'http:' || u.protocol === 'https:') && u.hostname.includes('.') && value.length <= 300; }
  catch { return false; }
}

export function buildIntakeSchema(z: any) {
  const req = (max: number, msg: string) => z.string().trim().min(1, msg).max(max, `Keep this under ${max} characters`);
  const opt = (max: number) => z.string().trim().max(max, `Keep this under ${max} characters`).default('');
  return z.object({
    startingPoint: z.enum(STARTING_POINTS, { errorMap: () => ({ message: 'Choose your starting point' }) }),
    company: opt(200),
    website: z.string().trim().max(300).default('').refine((v: string) => v === '' || isHttpUrl(v), 'Enter a valid website, like yourbrand.com'),
    primaryNeed: z.enum(PRIMARY_NEEDS, { errorMap: () => ({ message: 'Choose what you need most' }) }),
    adBudget: z.enum(AD_BUDGETS, { errorMap: () => ({ message: 'Choose your monthly ad budget' }) }),
    revenue: z.enum(REVENUES, { errorMap: () => ({ message: 'Choose a revenue range' }) }),
    channels: z.array(z.enum(CHANNELS)).min(1, 'Choose at least one option').max(CHANNELS.length),
    heardFrom: opt(200),
    goals: opt(GOALS_MAX),
    firstName: req(100, 'Enter your first name'),
    lastName: req(100, 'Enter your last name'),
    email: z.string().trim().min(1, 'Enter your business email').max(255).email('Enter a valid email address'),
    phone: z.string().trim().max(40).default('').refine((v: string) => v === '' || /^[+()\d\s.-]{7,40}$/.test(v), 'Enter a valid phone number'),
  }).superRefine((v: any, ctx: any) => {
    if (v.startingPoint && v.startingPoint !== 'New brand' && !v.company) ctx.addIssue({ code: 'custom', path: ['company'], message: 'Enter your company or brand name' });
    if (Array.isArray(v.channels) && v.channels.includes('Not running ads') && v.channels.length > 1) ctx.addIssue({ code: 'custom', path: ['channels'], message: '“Not running ads” can’t be combined with other channels' });
  });
}

export type IntakeAnswers = {
  startingPoint: string; company: string; website: string; primaryNeed: string; adBudget: string; revenue: string;
  channels: string[]; heardFrom: string; goals: string; firstName: string; lastName: string; email: string; phone: string;
};
